/**
 * createMarketingLoginAuth — factory for the cross-domain marketing-login auth
 * hook (MKT-LOGIN-XDOMAIN-001, 2026-06-29).
 *
 * The public marketing site has no AuthProvider, so the cross-domain login owns
 * its own auth state via a local MFA reducer + the app's authService, composed
 * over the shared `useLoginAuthAdapter`. Promoted from pos-web's hand-rolled
 * `useMarketingLoginAuth` so every marketing app wires it with ~5 lines of
 * dependency injection instead of copying ~210 lines.
 *
 * Returns a `useMarketingLoginAuth({ onSuccess, onAuthEvent })` hook. Each app's
 * wrapper calls the factory once at module scope:
 *
 *   const useMarketingLoginAuth = createMarketingLoginAuth({ authService });
 *
 * NO PASSKEY OPTION (USER-FACING-GEMS L1b, 2026-09-29). The factory used to take
 * `createPasskeyService` + `rpName` and hand the adapter a passkey service. No app
 * ever injected one that could work: a passkey is bound to the tenant origin's
 * rpId and the marketing apex holds none. Passkey sign-in lives on the tenant
 * login page (`usePasskeyLogin`), so this hook returns `{ auth }` only.
 *
 * SESSION HANDOFF (MKT-LOGIN-XDOMAIN-001/HANDOFF): this factory deliberately does
 * NOT persist tokens. The marketing origin and the tenant origin are different
 * origins (localStorage can't cross), so the login/verify result is handed to
 * `<MarketingCrossDomainLogin>`'s redirect step, which mints a one-time
 * `/v1/auth/cross-app/handoff` token; the tenant origin exchanges it into its
 * own session. Nothing is written to the marketing origin's storage.
 *
 * @module @puffin-core/login-shell/hooks/createMarketingLoginAuth
 */

import { useMemo, useReducer } from 'react';
import { useLoginAuthAdapter } from './useLoginAuthAdapter.js';
import { translateAuthError } from '../utils/errorTranslator.js';

const initialState = {
  isLoading: false,
  error: null,
  mfaRequired: false,
  mfaToken: null,
  mfaMethods: [],
  preferredMethod: null,
  maskedEmail: null,
  maskedPhone: null,
  // N152: a held sign-in (403 MFA_SETUP_REQUIRED) — `{ subdomain }` of the company whose own sign-in page enrols.
  mfaEnrollmentHandoff: null,
};

/**
 * MFA reducer. Exported for direct unit coverage of the unreachable `default`
 * arm (all in-hook dispatches use known action types).
 */
export function marketingLoginAuthReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: true, error: null };
    case 'SET_ERROR':
      return { ...state, isLoading: false, error: action.message };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'MFA_CHALLENGE':
      return {
        ...state,
        isLoading: false,
        error: null,
        mfaRequired: true,
        mfaToken: action.mfaToken,
        // The tenant login always answers both; a default invented a method the person may not hold (Q-20).
        mfaMethods: action.mfaMethods,
        preferredMethod: action.preferredMethod,
        maskedEmail: action.maskedEmail || null,
        maskedPhone: action.maskedPhone || null,
      };
    case 'MFA_ENROLLMENT_HANDOFF':
      return { ...initialState, mfaEnrollmentHandoff: { subdomain: action.subdomain } };
    case 'MFA_CANCEL':
    case 'SUCCESS':
      return { ...initialState };
    default:
      return state;
  }
}

/**
 * @param {object} config
 * @param {{login: Function, verifyMfa?: Function, sendMfaOtp?: Function}} config.authService
 *   REQUIRED. `login({email,password,subdomain,captchaToken})` resolving
 *   `{requiresMfa|accessToken|user|...}`; `verifyMfa({code,method,mfaToken})`
 *   resolving a result-object (never throws); `sendMfaOtp({method,mfaToken})`.
 * @returns {(params?: {onSuccess?: Function, onAuthEvent?: Function}) => {auth: object}}
 */
export function createMarketingLoginAuth({ authService } = {}) {
  if (!authService || typeof authService.login !== 'function') {
    throw new Error('createMarketingLoginAuth requires an authService with a login() function');
  }

  function useMarketingLoginAuth({ onSuccess, onAuthEvent } = {}) {
    const [state, dispatch] = useReducer(marketingLoginAuthReducer, initialState);

    const base = useMemo(
      () => ({
        login: async (email, password, captchaToken, extras) => {
          dispatch({ type: 'SET_LOADING' });
          const subdomain = extras?.subdomain || '';
          try {
            const result = await authService.login({ email, password, subdomain, captchaToken });
            if (result.requiresMfa) {
              dispatch({
                type: 'MFA_CHALLENGE',
                mfaToken: result.mfaToken,
                mfaMethods: result.mfaMethods,
                preferredMethod: result.preferredMethod,
                maskedEmail: result.maskedEmail,
                maskedPhone: result.maskedPhone,
              });
              return {
                requiresMfa: true,
                mfaMethods: result.mfaMethods,
                preferredMethod: result.preferredMethod,
              };
            }
            dispatch({ type: 'SUCCESS' });
            return result;
          } catch (err) {
            // Pre-translate for UI state; re-throw raw so the adapter translates
            // once more for the outward AuthError (preserving err.cause).
            const translated = translateAuthError(err, 'login');
            // N152: the password was right, but the person must enrol MFA again (Q-ARG-1). The apex cannot enrol them:
            // a completed enrolment stores its session on THIS origin, and the session handoff needs an access token
            // they do not have. They finish at their company's own sign-in page, where the shared enrolment runs.
            if (translated.errorClass === 'mfa_enrollment') {
              dispatch({ type: 'MFA_ENROLLMENT_HANDOFF', subdomain });
              return { requiresMfaEnrollmentHandoff: true };
            }
            dispatch({ type: 'SET_ERROR', message: translated.message });
            throw err;
          }
        },
        verifyMfa: async (code, method) => {
          if (!state.mfaToken) return undefined;
          dispatch({ type: 'SET_LOADING' });
          // authService.verifyMfa returns a result-object and never throws.
          const result = await authService.verifyMfa({ code, method, mfaToken: state.mfaToken });
          if (!result.success) {
            // The fleet vocabulary: the server's machine code is `errorCode` (`translateAuthError`
            // reads only that spelling since AUTH-ERROR-VOCABULARY-001).
            const err = Object.assign(new Error(result.error || 'MFA verification failed'), {
              status: result.status ?? null,
              errorCode: result.code ?? null,
              terminal: result.terminal ?? false,
            });
            const translated = translateAuthError(err, 'mfa');
            dispatch({ type: 'SET_ERROR', message: translated.message });
            throw err;
          }
          dispatch({ type: 'SUCCESS' });
          return result;
        },
        cancelMfa: () => dispatch({ type: 'MFA_CANCEL' }),
        clearError: () => dispatch({ type: 'CLEAR_ERROR' }),
        sendMfaOtp: async (method) => {
          if (!state.mfaToken) return;
          try {
            await authService.sendMfaOtp({ method, mfaToken: state.mfaToken });
          } catch (err) {
            const translated = translateAuthError(err, 'mfa');
            dispatch({ type: 'SET_ERROR', message: translated.message });
          }
        },
        isLoading: state.isLoading,
        error: state.error,
        mfaRequired: state.mfaRequired,
        mfaMethods: state.mfaMethods,
        preferredMethod: state.preferredMethod,
        maskedEmail: state.maskedEmail,
        maskedPhone: state.maskedPhone,
        mfaEnrollmentHandoff: state.mfaEnrollmentHandoff,
      }),
      [state],
    );

    return useLoginAuthAdapter({ base, onSuccess, onAuthEvent });
  }

  return useMarketingLoginAuth;
}

export default createMarketingLoginAuth;
